import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import LoginGuard from '../common/LoginGuard';
import { uploadReviewImage, validateImageFile, deleteReviewImage } from '../../lib/image-upload';
import { orderApi } from '../../lib/supabase-api';
import { Product } from '../../types';
import { useToast } from '../ui/Toast';

const ReviewForm: React.FC = () => {
  const { products, addReview } = useAdmin();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [formData, setFormData] = useState({
    productId: '',
    productName: '',
    userName: '',
    userEmail: '',
    rating: 5,
    title: '',
    content: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Image upload state
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Purchased products / other products separation
  const [purchasedProducts, setPurchasedProducts] = useState<Product[]>([]);
  const [otherProducts, setOtherProducts] = useState<Product[]>([]);

  // Auto-fill logged-in user email
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({
        ...prev,
        userEmail: user.email || '',
        userName: user.email || '' // Use email as display name
      }));
    }
  }, [user]);

  // Load purchased products
  useEffect(() => {
    const loadPurchasedProducts = async () => {
      if (!user?.id || products.length === 0) return;

      try {
        // Fetch user's order history
        const orders = await orderApi.getMyOrders();

        // Extract purchased product IDs
        const purchasedProductIds = new Set(
          orders.flatMap(order =>
            order.items.map(item => item.productId)
          )
        );

        // Separate products by purchase status
        const purchased = products.filter(p => purchasedProductIds.has(p.id));
        const others = products.filter(p => !purchasedProductIds.has(p.id));

        setPurchasedProducts(purchased);
        setOtherProducts(others);
      } catch (error) {
        console.error('Failed to load purchase history:', error);
        // Show all products on error
        setOtherProducts(products);
      }
    };

    loadPurchasedProducts();
  }, [user?.id, products]);

  // Image upload handler
  const handleImageUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (!user?.id) {
      toast.warning('Please log in first.');
      return;
    }

    // Max 5 images
    if (uploadedImages.length + files.length > 5) {
      toast.warning('You can upload up to 5 images.');
      return;
    }

    setIsUploading(true);
    setUploadProgress('Uploading images...');

    const newImages: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // File validation
      const validationError = validateImageFile(file);
      if (validationError) {
        toast.error(validationError);
        continue;
      }

      setUploadProgress(`Uploading ${i + 1}/${files.length}...`);

      const result = await uploadReviewImage(file, user.id);

      if (result.success && result.url) {
        newImages.push(result.url);
      } else {
        toast.error(`Image upload failed: ${result.error}`);
      }
    }

    setUploadedImages(prev => [...prev, ...newImages]);
    setIsUploading(false);
    setUploadProgress('');
  };

  // Remove image
  const handleImageRemove = async (imageUrl: string) => {
    setUploadedImages(prev => prev.filter(url => url !== imageUrl));
    await deleteReviewImage(imageUrl);
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleImageUpload(e.dataTransfer.files);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.productId) {
      newErrors.productId = 'Please select a product.';
    }
    // Email is auto-filled for logged-in users, no validation needed
    if (!formData.title.trim()) {
      newErrors.title = 'Please enter a review title.';
    }
    if (!formData.content.trim()) {
      newErrors.content = 'Please enter review content.';
    } else if (formData.content.trim().length < 10) {
      newErrors.content = 'Review content must be at least 10 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProductChange = (productId: string) => {
    // Search in both purchased and other products
    const selectedProduct = [...purchasedProducts, ...otherProducts].find(p => p.id === productId);
    setFormData(prev => ({
      ...prev,
      productId,
      productName: selectedProduct ? (selectedProduct.name || '') : ''
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare review data
      const reviewData = {
        productId: formData.productId,
        productName: formData.productName,
        userId: user?.id || '', // Use actual Supabase Auth UUID
        userName: formData.userName,
        userEmail: formData.userEmail,
        rating: formData.rating,
        title: formData.title,
        content: formData.content,
        images: uploadedImages, // Include uploaded images
        status: 'pending' as const, // Requires admin approval
      };

      const result = await addReview(reviewData);

      if (!result) {
        // Review submission failed (RLS block, etc.)
        toast.error('You can only write reviews for products you have purchased.\n\nPlease check your order history.');
        return;
      }

      // Success message and redirect to review page
      toast.success('Your review has been submitted successfully!\nIt will be published after admin approval.');
      navigate('/review');
    } catch (error) {
      console.error('Review submission error:', error);
      toast.error('An error occurred while submitting your review.\nPlease verify this is a product you have purchased.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating: number, onRatingChange?: (rating: number) => void) => {
    return [...Array(5)].map((_, i) => (
      <button
        key={i}
        type="button"
        onClick={() => onRatingChange && onRatingChange(i + 1)}
        className={`w-8 h-8 ${
          i < rating ? 'text-gold-400' : 'text-warm-300'
        } hover:text-gold-400 transition-colors ${
          onRatingChange ? 'cursor-pointer' : 'cursor-default'
        }`}
      >
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      </button>
    ));
  };

  return (
    <LoginGuard
      title="Write Review"
      message="Please log in to write a product review. Earn reward points by writing reviews!"
    >
    <div className="min-h-screen bg-cream-100">
      {/* Breadcrumb */}
      <div className="bg-cream-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm text-warm-500">
            <Link to="/" className="hover:text-gold-400">Home</Link>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <Link to="/review" className="hover:text-gold-400">Reviews</Link>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-forest-900 font-medium">Write Review</span>
          </nav>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-6 mb-6">
          <h1 className="text-2xl font-display font-bold text-forest-900 mb-2">Write a Product Review</h1>
          <p className="text-warm-600 mb-4">
            Share your honest feedback about the products you purchased. Earn reward points for writing reviews.
          </p>

          {/* Points Info */}
          <div className="bg-forest-50 rounded-xl p-4">
            <h3 className="font-medium text-forest-900 mb-2">Review Reward Benefits</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="font-bold text-gold-400">$5 credit</div>
                <div className="text-forest-700">Text Review</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-gold-400">$10 credit</div>
                <div className="text-forest-700">Photo Review</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-gold-400">$25 credit</div>
                <div className="text-forest-700">Best Review (monthly)</div>
              </div>
            </div>
          </div>
        </div>

        {/* Review Form */}
        <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Product Selection */}
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-2">
                Select Product <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.productId}
                onChange={(e) => handleProductChange(e.target.value)}
                className="w-full px-3 py-2 border border-warm-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
              >
                <option value="">Select a product to review</option>

                {/* Purchased products (shown first) */}
                {purchasedProducts.length > 0 && (
                  <optgroup label="Purchased Products">
                    {purchasedProducts.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </optgroup>
                )}

                {/* Other products */}
                {otherProducts.length > 0 && (
                  <optgroup label="Other Products">
                    {otherProducts.map(product => (
                      <option key={product.id} value={product.id} className="text-warm-500">
                        {product.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {errors.productId && (
                <p className="mt-1 text-sm text-red-600">{errors.productId}</p>
              )}
              {purchasedProducts.length > 0 && (
                <p className="mt-2 text-sm text-green-600">
                  Your {purchasedProducts.length} purchased product(s) are shown at the top
                </p>
              )}
            </div>

            {/* User Info - Display logged-in user info (read-only) */}
            <div className="bg-cream-100 rounded-xl p-4 border border-warm-200">
              <div className="flex items-center space-x-2 mb-2">
                <svg className="w-5 h-5 text-gold-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-sm font-medium text-forest-700">Author Information</span>
              </div>
              <p className="text-sm text-warm-600">
                Your review will be posted under <span className="font-medium">{formData.userEmail}</span>.
              </p>
              <p className="text-xs text-warm-500 mt-1">
                Your email will be <span className="font-medium">partially masked</span> when displayed (e.g., fre***@gmail.com)
              </p>
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-2">
                Rating <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-2">
                {renderStars(formData.rating, (rating) =>
                  setFormData(prev => ({ ...prev, rating }))
                )}
                <span className="text-sm text-warm-600 ml-2">
                  ({formData.rating}/5)
                </span>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-2">
                Review Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-warm-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
                placeholder="Write a brief summary of your review"
                maxLength={100}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-2">
                Review Content <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={8}
                maxLength={3000}
                className="w-full px-3 py-2 border border-warm-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600"
                placeholder="Share your honest experience with this product.&#10;Details about shipping, packaging, effectiveness, and satisfaction are very helpful for other customers."
              />
              <div className="flex justify-between items-center mt-2">
                {errors.content && (
                  <p className="text-sm text-red-600">{errors.content}</p>
                )}
                <p className="text-sm text-warm-500 ml-auto">
                  {formData.content.length} chars
                </p>
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-2">
                Attach Photos (optional)
                <span className="ml-2 text-xs text-gold-400 font-semibold">
                  Earn a $10 credit for a photo review!
                </span>
              </label>

              {/* Drag and drop area */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 ${
                  isDragging
                    ? 'border-forest-500 bg-forest-50'
                    : 'border-warm-200 hover:border-forest-600'
                }`}
              >
                <div className="flex flex-col items-center space-y-3">
                  <svg className="w-12 h-12 text-warm-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <label className="cursor-pointer">
                      <span className="text-gold-400 hover:text-gold-500 font-medium">
                        Choose files
                      </span>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                        multiple
                        onChange={(e) => handleImageUpload(e.target.files)}
                        className="hidden"
                        disabled={isUploading || uploadedImages.length >= 5}
                      />
                    </label>
                    <span className="text-warm-600"> or drag and drop images here</span>
                  </div>
                  <p className="text-xs text-warm-500">
                    JPG, PNG, WEBP, GIF (max 5MB, up to 5 files)
                  </p>
                </div>
              </div>

              {/* Upload progress */}
              {isUploading && (
                <div className="mt-3 flex items-center space-x-2 text-sm text-gold-400">
                  <div className="w-4 h-4 border-2 border-gold-400 border-t-transparent rounded-full animate-spin"></div>
                  <span>{uploadProgress}</span>
                </div>
              )}

              {/* Image preview */}
              {uploadedImages.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {uploadedImages.map((imageUrl, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={imageUrl}
                        alt={`Review image ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-warm-200"
                      />
                      <button
                        type="button"
                        onClick={() => handleImageRemove(imageUrl)}
                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove image"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {uploadedImages.length > 0 && (
                <p className="mt-2 text-sm text-green-600 font-medium">
                  {uploadedImages.length} image(s) uploaded ($10 credit will be earned)
                </p>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/review')}
                className="flex-1 px-6 py-3 border border-warm-200 text-forest-700 rounded-lg hover:bg-cream-100 font-medium transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-forest-800 text-cream-100 rounded-lg border border-forest-700/50 hover:bg-forest-700 font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </LoginGuard>
  );
};

export default ReviewForm;
